import type { KeyboardEvent } from 'react';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

/** Documented reasons a browser declines to show a picker: no user activation, or an unusable input. */
const EXPECTED_PICKER_REFUSALS = new Set(['NotAllowedError', 'InvalidStateError', 'NotSupportedError']);

/**
 * Opens a date/time input's own browser picker on plain ArrowDown, mirroring how a select opens its
 * menu. `showPicker` needs transient user activation, so it is called synchronously from the keydown
 * handler, and the default action is suppressed so ArrowDown does not also step the focused segment.
 *
 * Callers must opt in: on its own, ArrowDown steps the date segment, which some screens rely on.
 */
export const handleOpenPickerOnArrowDown = (event: KeyboardEvent<HTMLInputElement>, enabled: boolean): void => {
  const input = event.currentTarget;
  const opensPicker =
    enabled &&
    event.key === 'ArrowDown' &&
    !event.repeat &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    typeof input.showPicker === 'function';

  if (!opensPicker) {
    return;
  }

  event.preventDefault();

  try {
    input.showPicker();
  } catch (error) {
    if (error instanceof DOMException && EXPECTED_PICKER_REFUSALS.has(error.name)) {
      return;
    }
    ExceptionHandler.captureException(
      new InVivoError('Could not open the native date picker', {
        cause: error,
        slug: 'native-date-picker-show-picker',
      })
    );
  }
};
